import React, { useState } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import { OrderProduct, ProductApi, PricingTableObject } from 'common/types';
import classNames from 'classnames';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { RiAddLine, RiSubtractLine } from 'react-icons/ri';
import Button from '@mui/material/Button';
import AdditionalProductUnitsDialog from 'components/AdditionalProductUnitsDialog';
import { PartialOrderProductFields, newFirestoreId } from 'common/utils/newRentalUtils';
import VariantSelectorDialog from 'components/VariantSelectorDialog';
import AvailabilityStatus from 'components/AvailabilityStatus';
import { getTranslation } from 'common/modules/translations';
import { getLanguage } from 'services/localization/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { getItemPricingFromListPrice } from 'common/modules/atoms/pricing';
import { ProductTotalAvailability } from 'common/api/frontend/inventory_new';
import StripePattern from 'common/components/StripePattern';
import { Box, ButtonBase } from '@mui/material';

interface OwnProps {
	stockProduct: ProductApi;
	allStockProducts: ProductApi[];
	orderProducts: OrderProduct[];
	allOrderProducts: OrderProduct[];
	additionalProducts: ProductApi[];
	activeAdditionalProducts: OrderProduct[];
	addProduct: (product: OrderProduct) => void;
	removeProduct: (id: string) => void;
	updateAdditionalProducts: (products: OrderProduct[]) => void;
	partialOrderProductFields: PartialOrderProductFields;
	savedPricingTables?: PricingTableObject;
	startDate: string;
	endDate: string;
	shopId: string;
	productAvailability: ProductTotalAvailability | undefined;
	loadingProductAvailability: boolean;
	availabilityNotLoadedForLongDuration: boolean;
	currencyCode: string;
	taxExcluded: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const ActiveProductCard = (props: Props) => {
	const [variantDialogOpen, setVariantDialogOpen] = useState(false);
	const [additionalProductsOpen, setAdditionalProductsOpen] = useState(false);
	const { t } = useTranslation();
	const {
		classes,
		orderProducts,
		additionalProducts,
		activeAdditionalProducts,
		stockProduct,
		allStockProducts,
		partialOrderProductFields,
		productAvailability,
		availabilityNotLoadedForLongDuration,
		currencyCode,
		taxExcluded,
	} = props;
	const name = getTranslation(orderProducts[0].name, getLanguage());
	const units = orderProducts.length;
	const theme = useTheme();

	const isOverbooked =
		((productAvailability?.productAvailabilityWithOrderProducts ||
			productAvailability?.productAvailabilityWithOrderProducts === 0) &&
			productAvailability?.productAvailabilityWithOrderProducts < 0) ??
		false;

	const handleAddBtnClick = (add: boolean) => {
		const { orderProducts } = props;
		const orderProduct = orderProducts[orderProducts.length - 1];

		let setItemHasVariants = false;
		if (orderProduct.set && orderProduct.setProducts) {
			orderProduct.setProducts.forEach((setProduct) => {
				const setProductVariants = allStockProducts.filter(
					(p) => p.id === setProduct.productApiId,
				)[0]?.variants.options;
				if (!!setProductVariants && setProductVariants.length > 1 && setProduct.variant) {
					setItemHasVariants = true;
				}
			});
		}

		if (orderProduct.variant && stockProduct.variants.options.length > 1) {
			setVariantDialogOpen(!variantDialogOpen);
		} else if (orderProduct.set && setItemHasVariants) {
			setVariantDialogOpen(!variantDialogOpen);
		} else {
			if (add) {
				const newOrderProduct: OrderProduct = {
					...orderProduct,
					id: newFirestoreId(),
					pricing: getItemPricingFromListPrice(orderProduct.pricing.listPrice, {
						deposit: stockProduct.terms?.deposit || undefined,
						taxRate: stockProduct.vatPercent / 100,
						taxExcluded,
						currency: orderProduct.pricing.currency,
						manualDiscount: 0,
					}),
				};
				props.addProduct(newOrderProduct);
			} else {
				if (orderProducts.length) {
					props.removeProduct(orderProduct.id);
				}
			}
		}
	};

	const getProductUnitsCount = (products: OrderProduct[]) => {
		return products.length;
	};

	const handleAdditionalProductsClose = (products: OrderProduct[]) => {
		setAdditionalProductsOpen(false);
		props.updateAdditionalProducts(products);
	};

	const handleAdditionalProductsOpen = () => {
		setAdditionalProductsOpen(true);
	};

	return (
		<Card className={classNames(classes.productCard, classes.activeCard)} raised={true}>
			{isOverbooked && (
				<>
					<Box className={classes.overlay} />
					<StripePattern
						color="dark"
						opacity={0.2}
						degrees={135}
						stripeWidth={2}
						nonStripeColorOpacity={0.0}
					/>
				</>
			)}
			<CardContent className={classes.cardContent}>
				<div className={classes.nameContainer}>
					<Typography variant="body1">{name}</Typography>
				</div>
			</CardContent>
			<CardContent classes={{ root: classes.cardRoot }} className={classes.cardBody}>
				<div
					className={classNames(
						classes.productSelection,
						additionalProducts.length > 0 ? classes.productSelectionWithAdditionalProducts : '',
					)}
				>
					<Button
						className={classes.addButton}
						variant="text"
						onClick={() => handleAddBtnClick(false)}
					>
						<RiSubtractLine color={theme.palette.primary.main} size={34} />
					</Button>
					<Typography className={classes.addUnit} variant="h4" color="textPrimary" align="center">
						<b>{units}</b>
					</Typography>
					<Button
						className={classes.addButton}
						variant="text"
						onClick={() => handleAddBtnClick(true)}
					>
						<RiAddLine color={theme.palette.primary.main} size={34} />
					</Button>
					{variantDialogOpen && (
						<VariantSelectorDialog
							open={variantDialogOpen}
							orderProducts={orderProducts}
							allOrderProducts={props.allOrderProducts}
							onClose={() => setVariantDialogOpen(false)}
							stockProduct={stockProduct}
							allStockProducts={allStockProducts}
							partialOrderProductFields={partialOrderProductFields}
							addProduct={(orderProduct: OrderProduct) => props.addProduct(orderProduct)}
							savedPricingTables={props.savedPricingTables}
							removeProduct={(id: string) => props.removeProduct(id)}
							startDate={props.startDate}
							endDate={props.endDate}
							availability={productAvailability?.availabilityRangesByVariant}
							quantities={productAvailability?.variantQuantities}
							fullAvailability={productAvailability?.productAvailability}
							fullQuantity={productAvailability?.totalQuantity}
							availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
							currencyCode={currencyCode}
							taxExcluded={taxExcluded}
							isLoadingAvailability={props.loadingProductAvailability}
						/>
					)}
				</div>
				<div className={additionalProducts.length > 0 ? classes.availabilityStatusContainer : ''}>
					<AvailabilityStatus
						availability={productAvailability?.productAvailabilityWithOrderProducts}
						quantity={productAvailability?.totalQuantity}
						isLoadingAvailability={props.loadingProductAvailability}
						availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
					/>
				</div>
				{additionalProducts.length > 0 && (
					<>
						<ButtonBase
							className={classes.additionalProducts}
							onClick={handleAdditionalProductsOpen}
						>
							<Typography variant="button" className={classes.additionalProductsText}>
								{activeAdditionalProducts.length > 0
									? `${getProductUnitsCount(activeAdditionalProducts)} ${t(
											'newRental.hasAccessories',
											'accessories',
									  )}`
									: `+ ${t('newRental.addAccessories', 'Accessories')}`}
							</Typography>
						</ButtonBase>
						{additionalProductsOpen && (
							<AdditionalProductUnitsDialog
								open={additionalProductsOpen}
								onClose={handleAdditionalProductsClose}
								products={additionalProducts}
								activeProducts={activeAdditionalProducts}
								partialOrderProductFields={props.partialOrderProductFields}
								savedPricingTables={props.savedPricingTables}
								currencyCode={currencyCode}
								taxExcluded={taxExcluded}
							/>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		productCard: {
			position: 'relative',
			minHeight: '100%',
			display: 'flex',
			flexDirection: 'column',
			marginTop: '4px',
		},
		activeCard: {
			boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
		},
		cardContent: {
			zIndex: 2,
		},
		availabilityStatusContainer: {
			position: 'relative',
			left: 0,
			right: 0,
			bottom: '36px',
		},
		cardBody: {
			flex: 'none',
			padding: 0,
			paddingTop: 'calc(56.25% + 16px)',
		},
		relative: {
			position: 'relative',
		},
		cardRoot: {
			'&:last-child': {
				paddingBottom: 0,
			},
		},
		productSelection: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			position: 'absolute',
			width: '100%',
			padding: '0px 8px',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			margin: 'auto',
			zIndex: 2,
		},
		productSelectionWithAdditionalProducts: {
			bottom: 25,
		},
		flexColumn: {
			flexDirection: 'column',
		},
		additionalProducts: {
			textAlign: 'center',
			position: 'absolute',
			width: '100%',
			left: 0,
			right: 0,
			bottom: 0,
			margin: 'auto',
			background: theme.palette.primary.main,
			zIndex: 2,
			padding: theme.spacing(1),
		},
		additionalProductsText: {
			color: theme.palette.primary.contrastText,
		},
		addButton: {
			padding: 8,
			flex: '0 0 auto',
			minWidth: 0,
		},
		addUnit: {
			flex: '1 0 auto',
			minWidth: 32,
			maxWidth: 80,
		},
		overlay: {
			height: '100%',
			width: '100%',
			position: 'absolute',
			backgroundImage: `linear-gradient(to bottom, #fafafa, rgba(242, 242, 242, 0))`,
			zIndex: 1,
		},
		nameContainer: {
			textAlign: 'center',
		},
	});

export default withStyles(styles)(ActiveProductCard);
